import React, { useState } from 'react';
import { CheckCircle } from 'lucide-react';
import { useLanguage } from '../../../context/LanguageContext';
import { traccarFetchAll } from '../../gps/utils/traccar';

export default function GpsDevicePicker({ settings, value, onChange }) {
  const { lang } = useLanguage();
  const isAr = lang === 'ar';
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showList, setShowList] = useState(false);

  const loadDevices = async () => {
    if (!settings.traccarEnabled || !settings.traccarUrl || !settings.traccarEmail || !settings.traccarPassword) {
      setError(isAr ? '⚠️ يرجى إعداد GPS في الإعدادات أولاً' : "⚠️ Configurez GPS dans Paramètres d'abord");
      return;
    }
    
    setLoading(true);
    setError('');
    try {
      const result = await traccarFetchAll(settings);
      if (result.success) {
        setDevices(result.data);
        setShowList(true);
      } else {
        setError('❌ ' + (result.error || 'Erreur de connexion'));
      }
    } catch (err) {
      setError('❌ ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const selected = devices.find(d => 
    String(d.uniqueId) === String(value) || String(d.id) === String(value)
  );

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={value || ''}
          onChange={e => onChange(e.target.value)}
          placeholder={isAr ? 'معرف الجهاز أو IMEI' : 'IMEI ou ID du dispositif'}
          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg font-mono focus:ring-2 focus:ring-green-500"
        />
        <button
          type="button"
          onClick={loadDevices}
          disabled={loading}
          className="px-3 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white rounded-lg text-sm font-bold flex items-center gap-1.5 whitespace-nowrap transition"
        >
          {loading ? '⏳' : '📡'} {loading ? (isAr ? 'تحميل...' : 'Chargement...') : (isAr ? 'اختر' : 'Choisir')}
        </button>
      </div>

      {selected && (
        <div className="mt-1 p-2 bg-green-100 border border-green-300 rounded-lg text-xs flex items-center justify-between gap-2 text-green-950">
          <div>
            <div className="font-bold">✅ {selected.name}</div>
            <div className="font-mono text-[10px]">
              {`IMEI: ${selected.uniqueId}`}
              {selected.position && selected.position.latitude != null && selected.position.longitude != null
                ? ` • ${Number(selected.position.latitude).toFixed(4)}, ${Number(selected.position.longitude).toFixed(4)}`
                : ' • Aucune position'}
            </div>
          </div>
          <button
            type="button"
            onClick={() => onChange('')}
            className="text-red-600 hover:bg-red-100 p-1 rounded text-xs"
            title="Délier"
          >
            ✕
          </button>
        </div>
      )}

      {error && (
        <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg text-xs text-red-800">
          {error}
        </div>
      )}

      {showList && devices.length > 0 && (
        <div className="mt-2 border border-green-300 rounded-lg overflow-hidden">
          <div className="bg-green-100 px-3 py-2 text-xs font-bold text-green-900 flex items-center justify-between">
            <span>📡 {devices.length} {isAr ? 'أجهزة متاحة' : 'appareils disponibles'}</span>
            <button
              type="button"
              onClick={() => setShowList(false)}
              className="text-green-700 hover:text-green-900 text-base leading-none"
            >
              ×
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto bg-white">
            {devices.map(device => {
              const isSelected = String(device.uniqueId) === String(value) || String(device.id) === String(value);
              const isOnline = device.status === 'online';
              
              return (
                <button
                  key={device.id}
                  type="button"
                  onClick={() => {
                    const idToUse = device.uniqueId || String(device.id);
                    onChange(idToUse);
                    setShowList(false);
                  }}
                  className={`w-full px-3 py-2.5 text-start hover:bg-green-50 border-t border-green-100 transition flex items-center gap-2 ${
                    isSelected ? 'bg-green-100 border-l-4 border-l-green-500' : ''
                  }`}
                >
                  <span className="text-lg">{isOnline ? '🟢' : '⚫'}</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm text-gray-900 truncate">{device.name}</div>
                    <div className="text-[10px] text-gray-500 font-mono truncate">
                      {`ID: ${device.id} • IMEI: ${device.uniqueId}`}
                    </div>
                    {device.position && device.position.latitude != null && device.position.longitude != null ? (
                      <div className="text-[10px] text-green-700 font-mono mt-0.5">
                        {`📍 ${Number(device.position.latitude).toFixed(4)}, ${Number(device.position.longitude).toFixed(4)} • ${Math.round(device.position.speed || 0)} kph`}
                      </div>
                    ) : (
                      <div className="text-[10px] text-gray-400 mt-0.5">{isAr ? '⚠️ لا توجد وضعية' : '⚠️ Pas de position'}</div>
                    )}
                  </div>
                  {isSelected && <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
